"use client";

import { ReactNode, useState } from "react";
import { Trans } from "@lingui/react/macro";
import {
  CrossAppLink,
  useCrossAppHrefResolver,
} from "@/components/cross-app-link";

import { TComponentProps } from "@/types";

import { Text } from "@/components/atoms/text";
import { Container } from "@/components/atoms/container";
import { useFooterData, TFooterLink } from "./fixture";

import { cn } from "@marketing-sites/utils/cn";
import { triggerAnalyticsLinkClick } from "@marketing-sites/analytics/trigger-analytics-event";
import { PrivacyOptionsIcon } from "@marketing-sites/ui/privacy-options-icon";
import {
  EnglishFallbackNotice,
  LocaleSwitcher,
  LocaleSwitcherLinks,
  useLocaleSwitcherHeading,
} from "@/components/molecules/locale-switcher";
import { LOCALE_UI_ENABLED } from "@/lib/l10n/config";

const LOCALE_ACCORDION_ID = "locale-switcher";

export type IntercomFooterProps = TComponentProps & {
  blurWrapper?: boolean;
  isThemeDark?: boolean;
  /**
   * Drop the sitemap nav columns (desktop grid + mobile accordion), leaving only
   * the legal/info row. Used by DRLP landing pages, which shouldn't surface the
   * full sitemap.
   */
  minimalFooter?: boolean;
  /** Override the legal/info links row (defaults to the global `footerInfoLinks`). */
  infoLinks?: TFooterLink[];
};

export const IntercomFooter = ({
  className,
  dataTestId,
  blurWrapper,
  isThemeDark = false,
  minimalFooter = false,
  infoLinks,
}: IntercomFooterProps) => {
  const [openAccordionId, setOpenAccordionId] = useState<string | null>(null);
  const { footerData, footerInfoLinks } = useFooterData();
  const resolvedInfoLinks = infoLinks ?? footerInfoLinks;
  const localeHeading = useLocaleSwitcherHeading();

  return (
    <div
      className={cn(
        "relative isolate mx-auto max-w-[1568px] overflow-hidden py-5",
        isThemeDark ? "bg-black" : "bg-white",
      )}
    >
      {/* background is now handled by the parent element */}
      <footer
        className={cn(
          "z-1 relative w-full overflow-hidden",
          "flex flex-col gap-0",
          isThemeDark ? "text-white/60" : "text-black/60",
          className || "bg-transparent",
          blurWrapper &&
            "mx-auto mb-4 w-[calc(100%-2rem)] max-w-[1568px] bg-white/10 pt-1 backdrop-blur-lg md:pt-6",
        )}
        data-testid={dataTestId}
      >
        {LOCALE_UI_ENABLED && <EnglishFallbackNotice />}

        {!minimalFooter && (
          <Container
            size="lg"
            className="hidden grid-cols-2 gap-6 md:grid md:grid-cols-5 md:px-6"
          >
            {footerData.map((column, columnIndex) => (
              <div key={column.categories[0].title}>
                {column.categories.map((category) => (
                  <FooterListSection
                    key={category.title}
                    title={category.title}
                  >
                    {category.links.map((link) => (
                      <FooterListSectionItem
                        key={link.title}
                        href={link.href}
                        text={link.title}
                        analyticsText={link.analyticsText}
                        // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the call site
                        analyticsId={link.analyticsId}
                        isThemeDark={isThemeDark}
                      >
                        {link.title}
                      </FooterListSectionItem>
                    ))}
                  </FooterListSection>
                ))}
                {LOCALE_UI_ENABLED && columnIndex === footerData.length - 1 && (
                  <LocaleSwitcher isThemeDark={isThemeDark} />
                )}
              </div>
            ))}
          </Container>
        )}

        {/* Mobile Accordion Footer */}
        {!minimalFooter && (
          <Container size="lg" className="flex flex-col md:hidden">
            {footerData.map((column) => (
              <div key={column.categories[0].title}>
                {column.categories.map((category) => (
                  <MobileFooterAccordion
                    key={category.title}
                    id={category.title}
                    title={category.title}
                    isThemeDark={isThemeDark}
                    links={category.links.map((link) => ({
                      href: link.href,
                      label: link.title,
                      analyticsText: link.analyticsText,
                      // eslint-disable-next-line analytics/id-must-be-static -- each item in footerData carries a hardcoded id
                      analyticsId: link.mobileMetadata.analyticsId,
                    }))}
                    isOpen={openAccordionId === category.title}
                    onToggle={() => {
                      setOpenAccordionId(
                        openAccordionId === category.title
                          ? null
                          : category.title,
                      );
                    }}
                  />
                ))}
              </div>
            ))}
            {LOCALE_UI_ENABLED && (
              <MobileFooterAccordionShell
                title={localeHeading}
                isThemeDark={isThemeDark}
                isOpen={openAccordionId === LOCALE_ACCORDION_ID}
                onToggle={() => {
                  setOpenAccordionId(
                    openAccordionId === LOCALE_ACCORDION_ID
                      ? null
                      : LOCALE_ACCORDION_ID,
                  );
                }}
              >
                <LocaleSwitcherLinks isThemeDark={isThemeDark} />
              </MobileFooterAccordionShell>
            )}
          </Container>
        )}

        <Container
          size="lg"
          id="footer-terms-and-conditions"
          className={cn(
            "flex flex-wrap justify-between pt-8",
            blurWrapper && "md:mb-4 md:px-6",
          )}
        >
          <div className="flex flex-col flex-wrap items-center gap-x-4 md:flex-row">
            <div className="flex flex-row gap-3">
              {resolvedInfoLinks.map((link) => (
                <FooterInfoSection
                  key={link.title}
                  title={link.title}
                  href={link.href}
                  analyticsText={link.analyticsText}
                  // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the call site
                  analyticsId={link.analyticsId}
                  isThemeDark={isThemeDark}
                />
              ))}
            </div>

            <div className="flex items-center">
              <PrivacyOptionsIcon />
              <Text
                asChild
                className={cn(
                  "!border-none !bg-transparent !px-2 !py-3 !text-base",
                  isThemeDark
                    ? "!text-white/60 hover:!text-white"
                    : "!text-black/60 hover:!text-black",
                )}
              >
                <button
                  id="ot-sdk-btn"
                  className="ot-sdk-show-settings"
                  onClick={() => {
                    window.Optanon?.ToggleInfoDisplay();
                  }}
                >
                  <Trans>Your Privacy Choices</Trans>
                </button>
              </Text>
            </div>
          </div>
        </Container>
      </footer>
    </div>
  );
};

const MobileFooterAccordionShell = ({
  title,
  isOpen,
  onToggle,
  isThemeDark,
  children,
}: {
  title: string;
  isOpen: boolean;
  onToggle: () => void;
  isThemeDark?: boolean;
  children: ReactNode;
}) => (
  <div
    className={cn(
      "border-b",
      isThemeDark ? "border-white/20" : "border-black/20",
    )}
  >
    <button
      onClick={onToggle}
      className="flex w-full items-center justify-between py-3.5 text-left"
    >
      <span
        data-slot="mobile-tab"
        className={cn(
          "text-xs uppercase leading-[130%] tracking-[1.2px]",
          isThemeDark ? "text-white" : "text-black",
        )}
      >
        {title}
      </span>
      <span
        className={cn(
          "transform text-lg leading-[1] transition-transform",
          isThemeDark ? "text-white" : "text-black",
        )}
      >
        {isOpen ? "▲" : "▼"}
      </span>
    </button>

    {isOpen && children}
  </div>
);

const MobileFooterAccordion = ({
  title,
  links,
  isOpen,
  onToggle,
  isThemeDark,
}: {
  id: string;
  title: string;
  links: Array<{
    href: string;
    label: string;
    analyticsText?: string;
    analyticsId: string;
  }>;
  isOpen: boolean;
  onToggle: () => void;
  isThemeDark?: boolean;
}) => {
  const resolveHref = useCrossAppHrefResolver();

  return (
    <MobileFooterAccordionShell
      title={title}
      isOpen={isOpen}
      onToggle={onToggle}
      isThemeDark={isThemeDark}
    >
      <ul className="mb-5 flex flex-col gap-1">
        {links.map((link) => (
          <li key={link.label}>
            <Text
              anchor
              animateIn
              asChild
              className={cn(
                "block py-0.5 text-base opacity-90 hover:opacity-100 focus:opacity-100",
                isThemeDark ? "text-white" : "text-black",
              )}
            >
              <CrossAppLink
                href={link.href}
                onClick={() => {
                  triggerAnalyticsLinkClick({
                    // eslint-disable-next-line analytics/id-must-be-static -- forwarded from the call site
                    analyticsId: link.analyticsId,
                    text: link.analyticsText ?? link.label,
                    destination: resolveHref(link.href),
                    location: "Footer",
                  });
                }}
              >
                {link.label}
              </CrossAppLink>
            </Text>
          </li>
        ))}
      </ul>
    </MobileFooterAccordionShell>
  );
};

const FooterListSection = ({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) => {
  return (
    <div className="flex flex-col gap-3 pb-8">
      <span
        data-slot="label"
        className="text-xs font-medium uppercase leading-[130%] tracking-[1.2px]"
      >
        {title}
      </span>
      <ul className="flex flex-col gap-1">{children}</ul>
    </div>
  );
};

const FooterListSectionItem = ({
  children,
  href,
  text,
  analyticsText,
  analyticsId,
  isThemeDark,
}: {
  children: ReactNode;
  href: string;
  text: string;
  analyticsText?: string;
  analyticsId: string;
  isThemeDark?: boolean;
}) => {
  const resolveHref = useCrossAppHrefResolver();

  return (
    <li>
      <Text
        anchor
        animateIn
        asChild
        className={cn(
          "text-base leading-[125%] opacity-90 hover:opacity-100 focus:opacity-100",
          isThemeDark ? "text-white" : "text-black",
        )}
      >
        <CrossAppLink
          href={href}
          onClick={() => {
            triggerAnalyticsLinkClick({
              analyticsId: analyticsId,
              text: analyticsText ?? text,
              destination: resolveHref(href),
              location: "Footer",
            });
          }}
        >
          {children}
        </CrossAppLink>
      </Text>
    </li>
  );
};

const FooterInfoSection = ({
  title,
  href,
  analyticsText,
  analyticsId,
  isThemeDark,
}: {
  title: string;
  href: string;
  analyticsText?: string;
  analyticsId: string;
  isThemeDark?: boolean;
}) => {
  const resolveHref = useCrossAppHrefResolver();

  return (
    <CrossAppLink
      className={cn(
        "text-base",
        isThemeDark
          ? "text-white/60 hover:text-white focus:text-white"
          : "text-black/60 hover:text-black focus:text-black",
      )}
      href={href}
      onClick={() => {
        triggerAnalyticsLinkClick({
          analyticsId: analyticsId,
          text: analyticsText ?? title,
          destination: resolveHref(href),
          location: "Footer",
        });
      }}
    >
      {title}
    </CrossAppLink>
  );
};
